import { Link } from "react-router";
import { Check } from "lucide-react";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { Reveal } from "../components/motion";
import type { SectionProps } from "./SectionRenderer";

const HEIGHTS: Record<string, string> = {
  compact: "py-16 md:py-20",
  standard: "py-24 md:py-32",
  tall: "py-28 md:py-40",
  fullscreen: "min-h-[80vh] flex items-center py-24",
};

const FOCAL: Record<string, string> = {
  center: "object-center",
  top: "object-top",
  bottom: "object-bottom",
  left: "object-left",
  right: "object-right",
};

/**
 * The darkening layer, weighted towards whichever side the copy sits on.
 *
 * A two-stop gradient rather than a flat wash: the wash darkened the empty
 * side of the frame exactly as hard as the words, which turned every
 * photograph into a grey field. Centred copy has no quiet side to protect, so
 * it keeps an even veil.
 */
const OVERLAY: Record<string, string> = {
  left: "bg-gradient-to-r from-navy-950 via-navy-950/70 to-navy-950/20",
  right: "bg-gradient-to-l from-navy-950 via-navy-950/70 to-navy-950/20",
  center: "bg-navy-950",
};

/**
 * The opening section of a page.
 *
 * Renders its own shell rather than using SectionShell: a hero needs a
 * background image layer and its own height scale, neither of which the shared
 * shell should carry for the other fourteen blocks.
 */
export default function HeroBlock({ content, settings }: SectionProps<BlockContentMap["hero"]>) {
  const isSplit = content.layout === "imageLeft" || content.layout === "imageRight";
  const hasBackgroundImage = !isSplit && Boolean(content.image?.mediaId);

  /**
   * A hero stored before these fields existed does not carry them: the API
   * parses a section's `settings` on read but passes its `content` through as
   * it was written. So the default is applied here too, the same way
   * `HEIGHTS[content.height]` already falls back — without it an un-migrated
   * hero would take `undefined / 100` into the overlay's opacity, and an
   * invalid opacity paints the veil solid over the headline it is protecting.
   */
  const overlayOpacity = content.overlayOpacity ?? 55;

  return (
    <section
      id={settings.anchorId || undefined}
      className={cn(
        "relative overflow-hidden bg-navy-900 text-cream-100",
        settings.customClassName,
      )}
    >
      {hasBackgroundImage && content.image && (
        <div className="absolute inset-0">
          <ResponsiveImage
            image={content.image}
            className={cn(
              "size-full object-cover",
              FOCAL[content.focalPoint] ?? FOCAL.center,
              // Mirrors the photograph only. Applied to the image element
              // rather than its wrapper so the gradient overlay, copy and
              // buttons above it are unaffected.
              content.flipHorizontal && "-scale-x-100",
            )}
            priority
            sizes="100vw"
          />
          {/* Keeps the headline readable over whatever photograph an editor
              uploads later, which is the whole reason it exists. */}
          <div
            aria-hidden
            className={cn("absolute inset-0", OVERLAY[settings.alignment] ?? OVERLAY.left)}
            style={{ opacity: overlayOpacity / 100 }}
          />
          {/* A constant vertical veil on top of it, anchoring the copy to the
              floor of the frame and keeping the header legible against a
              bright sky. Not editor-controlled: it is structural. */}
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-navy-950/30"
          />
        </div>
      )}

      <div
        className={cn(
          "relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10",
          HEIGHTS[content.height] ?? HEIGHTS.standard,
        )}
      >
        <div
          className={cn(
            "grid gap-12 lg:items-center",
            isSplit ? "lg:grid-cols-2" : "",
            content.layout === "imageLeft" && "lg:[&>*:first-child]:order-2",
          )}
        >
          {/*
            A hero is almost always the first thing in the viewport, so this
            plays on load rather than on scroll. It is the one reveal a visitor
            is guaranteed to see, which is reason enough for it to exist.
          */}
          <Reveal
            className={cn(
              settings.alignment === "center" && "mx-auto max-w-3xl text-center",
              !isSplit && settings.alignment !== "center" && "max-w-3xl",
            )}
          >
            {content.eyebrow && (
              <p className="text-xs font-semibold uppercase tracking-wider text-gold-300">
                {content.eyebrow}
              </p>
            )}

            <h1 className="mt-4 text-6xl text-cream-50">{content.heading}</h1>

            {content.subheading && (
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-navy-100 md:text-xl">
                {content.subheading}
              </p>
            )}

            {content.buttons.length > 0 && (
              <div
                className={cn(
                  "mt-9 flex flex-wrap gap-3",
                  settings.alignment === "center" && "justify-center",
                )}
              >
                {content.buttons.map((button, index) => (
                  <Link
                    key={button.href + button.label}
                    to={button.href}
                    {...(button.opensInNewTab
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className={cn(
                      "rounded-md px-7 py-3.5 text-sm font-semibold transition-colors",
                      index === 0 || button.variant === "primary"
                        ? "bg-gold-500 text-navy-900 hover:bg-gold-400"
                        : "text-cream-100 ring-1 ring-inset ring-cream-100/30 hover:bg-cream-100/10",
                    )}
                  >
                    {button.label}
                  </Link>
                ))}
              </div>
            )}

            {content.highlights.length > 0 && (
              <ul
                className={cn(
                  "mt-10 flex flex-wrap gap-x-7 gap-y-3",
                  settings.alignment === "center" && "justify-center",
                )}
              >
                {content.highlights.map((highlight) => (
                  <li key={highlight} className="flex items-center gap-2 text-sm text-navy-200">
                    <Check aria-hidden className="size-4 shrink-0 text-gold-400" />
                    {highlight}
                  </li>
                ))}
              </ul>
            )}
          </Reveal>

          {isSplit && content.image && (
            <Reveal delay={0.15}>
              <ResponsiveImage
                image={content.image}
                className={cn(
                  "w-full rounded-xl object-cover shadow-xl",
                  FOCAL[content.focalPoint] ?? FOCAL.center,
                  content.flipHorizontal && "-scale-x-100",
                )}
                priority
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
